import { useCallback, useEffect, useState } from "react";
import { useRecoilState } from "recoil";

import { LearningArticleStatus } from "@/api/generated";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { LearningArticleStatusState, LearningArticleStatusMap } from "@/view_models/atoms";

const toStatusMap = (statuses: LearningArticleStatus[]): LearningArticleStatusMap =>
  statuses.reduce<LearningArticleStatusMap>((acc, status) => {
    if (status.article_id) {
      acc[status.article_id] = status;
    }
    return acc;
  }, {});

export const useLearningProgress = () => {
  const [statusMap, setStatusMap] = useRecoilState(LearningArticleStatusState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fp = PropallyApiFactory();
      const res = await fp.v1LearningArticlesStatusesGet({
        withCredentials: true,
      });
      setStatusMap(toStatusMap(res.data));
    } catch (e) {
      setError("学習状況の取得に失敗しました。時間をおいて再度お試しください。");
    } finally {
      setLoading(false);
    }
    setInitialized(true);
  }, [setStatusMap]);

  const markAsCompleted = useCallback(
    async (articleId: string) => {
      try {
        const fp = PropallyApiFactory();
        const res = await fp.v1LearningArticlesArticleIdCompletePost(articleId, {
          withCredentials: true,
        });
        setStatusMap((prev) => ({
          ...prev,
          ...(res.data.article_id ? { [res.data.article_id]: res.data } : {}),
        }));
      } catch (e) {
        setError("学習状況の更新に失敗しました。再度お試しください。");
        throw e;
      }
    },
    [setStatusMap]
  );

  useEffect(() => {
    // まだデータがない場合のみ初回取得
    if (!initialized) {
      fetchStatuses();
    }
  }, [fetchStatuses, initialized]);

  return {
    statusMap,
    initialized,
    loading,
    error,
    refetch: fetchStatuses,
    markAsCompleted,
  };
};
